import { useEffect } from 'react'

import { Box, SelectOption } from '@factorialco/deprecated-design-system'
import { LeavesFormEmployeeSelectorDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import RadioGroup from 'modules/core/components/Inputs/RadioGroup'
import Select from 'modules/core/components/Inputs/Select'
import TextArea from 'modules/core/components/Inputs/TextArea'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'
import DayTypeForm from 'modules/timeoff/components/LeaveContent/OtherLeavesForm/DayTypeForm'
import LeaveTypeFormInput from 'modules/timeoff/components/LeaveTypeFormInput'
import { LabourUnitsResult } from 'modules/timeoff/hooks/useLabourUnits'
import type LeaveDayTypeForm from 'modules/timeoff/lib/LeaveDayTypeForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { DaysTree } from 'modules/timeoff/lib/getDateRangeFromTree'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

interface Props {
  readOnly?: boolean
  leaveTypeReadOnly: boolean
  form: LeaveForm
  dayTypeForm: LeaveDayTypeForm
  dayTypeOptions: SelectOption[]
  companyHolidaysMuncher: () => DaysTree
  blockedPeriodsMuncher: () => DaysTree
  selectedLeaveType?: LeaveType
  timeTaken?: string
  labourUnits: LabourUnitsResult
  isReviewOrEditMode?: boolean
  policyFieldOptions: SelectOption[]
  leaveTypesForPolicy: LeaveType[]
  leaveId: number | undefined
  leaveTypeId: number
}

const LeavesForm = ({
  readOnly = false,
  leaveTypeReadOnly,
  form,
  dayTypeForm,
  dayTypeOptions,
  companyHolidaysMuncher,
  blockedPeriodsMuncher,
  selectedLeaveType,
  timeTaken,
  labourUnits,
  isReviewOrEditMode,
  policyFieldOptions,
  leaveTypesForPolicy,
  leaveId,
  leaveTypeId,
}: Props) => {
  const isMandatory = selectedLeaveType?.attributes.details_required
  const policyField = form.get('policy_id')
  const policyId = Number(policyField.value)
  const hasPolicyId = Number.isInteger(policyId) && policyId > 0

  const { data: employeeSelectorData, refetch: refetchEmployeeSelector } = useQuery(
    LeavesFormEmployeeSelectorDocument,
    {
      variables: {
        policyId,
      },
      skip: !hasPolicyId,
    }
  )

  useEffect(() => {
    if (!hasPolicyId) return

    refetchEmployeeSelector({
      policyId,
    })
  }, [hasPolicyId, policyId, refetchEmployeeSelector])

  const employeeIds = hasPolicyId
    ? employeeSelectorData?.employees.employeeNames.map((employee) => employee.id) ?? []
    : []

  return (
    <>
      <FormRow>
        <Select
          label={i18n.t('common.policy')}
          field={policyField}
          options={policyFieldOptions}
          showOriginalLabelText
        />
      </FormRow>
      <LeaveTypeFormInput
        form={form}
        availableLeaveTypes={leaveTypesForPolicy}
        readOnly={readOnly || leaveTypeReadOnly}
        selectedLeaveType={selectedLeaveType}
      />
      <Box gap="s8" width="s400" paddingBottom="s24">
        <EmployeeSelector
          label={i18n.t('common.employee')}
          employeeIds={employeeIds}
          multiple
          disabled={!hasPolicyId}
          value={form.get('employees_for_bulk_creation').value || []}
          onSelect={(value) => {
            form.get('employees_for_bulk_creation').set(value)
          }}
        />
      </Box>
      <FormRow>
        <TextArea
          maxLength={250}
          readonly={readOnly}
          label={i18n.t('time_off.description')}
          field={form.get('description')}
          mandatory={isMandatory}
        />
      </FormRow>
      <FormRow>
        <RadioGroup
          invisibleRadio
          name="dayType"
          theme="toggle"
          readonly={readOnly}
          field={dayTypeForm.get('day_type')}
          options={dayTypeOptions}
        />
      </FormRow>
      <DayTypeForm
        readOnly={readOnly}
        form={form}
        dayTypeForm={dayTypeForm}
        companyHolidaysMuncher={companyHolidaysMuncher}
        blockedPeriodsMuncher={blockedPeriodsMuncher}
        timeTaken={timeTaken}
        labourUnits={labourUnits}
        isReviewOrEditMode={isReviewOrEditMode}
        leaveId={leaveId}
        leaveTypeId={leaveTypeId}
      />
    </>
  )
}

export default observer(LeavesForm)
